import React, { useState, useRef } from 'react';
import { getAssetUrl } from '../../lib/utils';

interface ImageUploadProps {
  images: string[];
  newImages: File[];
  onImagesChange: (images: string[]) => void;
  onNewImagesChange: (newImages: File[]) => void;
  onImageDelete?: (imageUrl: string) => Promise<void>;
  maxImages?: number;
  className?: string;
}

const ImageUpload: React.FC<ImageUploadProps> = ({
  images,
  newImages,
  onImagesChange,
  onNewImagesChange,
  onImageDelete,
  maxImages = 5,
  className = ''
}) => {
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFiles = (files: FileList | null) => {
    if (!files) return;

    const fileArray = Array.from(files);
    const imageFiles = fileArray.filter(file => file.type.startsWith('image/'));
    
    if (imageFiles.length === 0) {
      alert('Please select only image files');
      return;
    }

    const totalImages = images.length + newImages.length + imageFiles.length;
    if (totalImages > maxImages) {
      alert(`Maximum ${maxImages} images allowed`);
      return;
    }

    // Check file sizes (5MB limit)
    const oversizedFiles = imageFiles.filter(file => file.size > 5 * 1024 * 1024);
    if (oversizedFiles.length > 0) {
      alert('Some files are larger than 5MB. Please select smaller files.');
      return;
    }

    onNewImagesChange([...newImages, ...imageFiles]);
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFiles(e.target.files);
  };

  const removeExistingImage = async (index: number) => {
    const imageToDelete = images[index];
    const newImages = images.filter((_, i) => i !== index);
    
    // Update local state immediately for better UX
    onImagesChange(newImages);
    
    // Call the delete API if callback is provided
    if (onImageDelete && imageToDelete) {
      try {
        await onImageDelete(imageToDelete);
      } catch (error) {
        // Revert the local state if deletion failed
        onImagesChange(images);
      }
    }
  };

  const removeNewImage = (index: number) => {
    const newNewImages = newImages.filter((_, i) => i !== index);
    onNewImagesChange(newNewImages);
  };

  const getImageUrl = (image: string) => {
    if (image.startsWith('http')) {
      return image;
    }
    // Use our asset URL utility for static file serving
    return getAssetUrl(image);
  };

  return (
    <div className={`space-y-6 ${className}`}>
      <div className="flex items-center justify-between">
        <label className="block text-lg font-semibold text-gray-800">
          Temple Gallery
        </label>
        <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
          {images.length + newImages.length} / {maxImages} images
        </span>
      </div>
      
      {/* Upload Area */}
      <div
        className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-all duration-200 ${
          dragActive
            ? 'border-indigo-400 bg-indigo-50 scale-105'
            : 'border-gray-300 hover:border-indigo-300 hover:bg-gray-50'
        }`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <div className="space-y-4">
          <div className="mx-auto w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center">
            <svg
              className="w-8 h-8 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 6v6m0 0v6m0-6h6m-6 0H6"
              />
            </svg>
          </div>
          
          <div className="space-y-2">
            <h3 className="text-lg font-medium text-gray-900">
              Upload Temple Images
            </h3>
            <p className="text-sm text-gray-600">
              Drag and drop your images here, or{' '}
              <label
                htmlFor="file-upload"
                className="relative cursor-pointer font-medium text-indigo-600 hover:text-indigo-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-indigo-500"
              >
                <span>browse files</span>
                <input
                  id="file-upload"
                  ref={fileInputRef}
                  type="file"
                  className="sr-only"
                  multiple
                  accept="image/*"
                  onChange={handleFileInput}
                />
              </label>
            </p>
          </div>
          
          <div className="flex items-center justify-center space-x-6 text-xs text-gray-500">
            <div className="flex items-center space-x-1">
              <div className="w-2 h-2 bg-green-400 rounded-full"></div>
              <span>PNG, JPG, GIF, WebP</span>
            </div>
            <div className="flex items-center space-x-1">
              <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
              <span>Max 5MB each</span>
            </div>
            <div className="flex items-center space-x-1">
              <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
              <span>Up to {maxImages} images</span>
            </div>
          </div>
        </div>
      </div>

      {/* Image Preview */}
      {(images.length > 0 || newImages.length > 0) && (
        <div className="space-y-4">
          <h4 className="text-md font-medium text-gray-700 flex items-center">
            <svg className="w-5 h-5 mr-2 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            Uploaded Images
          </h4>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {/* Existing Images */}
            {images.map((image, index) => (
              <div key={`existing-${index}`} className="relative group bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow duration-200">
                <div className="aspect-square relative">
                  <img
                    src={getImageUrl(image)}
                    alt={`Temple ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200"></div>
                  
                  {/* Delete Button */}
                  <button
                    type="button"
                    onClick={() => removeExistingImage(index)}
                    className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-7 h-7 flex items-center justify-center text-sm hover:bg-red-600 opacity-0 group-hover:opacity-100 transition-all duration-200 shadow-lg"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                  
                  {/* Status Badge */}
                  <div className="absolute bottom-2 left-2 bg-blue-500 text-white text-xs px-2 py-1 rounded-full font-medium">
                    Existing
                  </div>
                </div>
              </div>
            ))}

            {/* New Images */}
            {newImages.map((file, index) => (
              <div key={`new-${index}`} className="relative group bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow duration-200">
                <div className="aspect-square relative">
                  <img
                    src={URL.createObjectURL(file)}
                    alt={`New ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200"></div>
                  
                  {/* Delete Button */}
                  <button
                    type="button"
                    onClick={() => removeNewImage(index)}
                    className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-7 h-7 flex items-center justify-center text-sm hover:bg-red-600 opacity-0 group-hover:opacity-100 transition-all duration-200 shadow-lg"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                  
                  {/* Status Badge */}
                  <div className="absolute bottom-2 left-2 bg-green-500 text-white text-xs px-2 py-1 rounded-full font-medium">
                    New
                  </div>
                  
                  {/* Upload Progress Indicator */}
                  <div className="absolute top-2 left-2 bg-yellow-400 text-yellow-900 text-xs px-2 py-1 rounded-full font-medium animate-pulse">
                    Pending
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Image Count */}
      <div className="text-sm text-gray-500">
        {images.length + newImages.length} / {maxImages} images selected
      </div>
    </div>
  );
};

export default ImageUpload;
